/** The stored contacts' facts the contacts value does not carry — each contact's OTA and
 *  its created / updated times — from the kept `GET /users/<id>/contacts/` (the plan keeps
 *  the list endpoint; only its writes retire). Refetched after a save (`userContactsKey`). */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { userContactsKey } from '@/api/userRecord';
import * as usersApi from '@/api/users';
import type { ContactMeta } from '@/features/records/fields/widgets/ContactsWidget';

/** More than any person has (the backend caps a list at 500 contacts). */
const PAGE = 200;

export function useContactsMeta(userId: number, enabled = true): ReadonlyMap<number, ContactMeta> | undefined {
  const q = useQuery({
    queryKey: userContactsKey(userId),
    queryFn: () => usersApi.listContacts(userId, { pageSize: PAGE }),
    enabled,
    staleTime: 60_000,
  });
  return useMemo(() => {
    if (!q.data) return undefined;
    return new Map(
      q.data.results.map((c) => [c.id, { ota: c.ota, created_ts: c.created_ts, updated_ts: c.updated_ts } satisfies ContactMeta]),
    );
  }, [q.data]);
}
